import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Spinner, Toast, InputGroup } from 'react-bootstrap';
import { Globe, Settings, Play, Code, Clock } from 'lucide-react';
import type {
    ApiResponseData,
    ApiSourceConfig,
    ApiSourceFormData,
    FormConfig,
    ApiTestResponse,
    ApiResponseValue
} from '../../../types';
import { testAPI, formatTestResult } from '../../../services/testAPI';
import InlineResponseViewer from '../../../components/data-sources/api-sources/InlineResponseViewer';

const DEFAULT_CONFIG = {
    requests_per_minute: 60,
    retry_count: 3,
    timeout_seconds: 30,
    max_response_size_mb: 10
};

type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
type JsonArray = JsonValue[];
interface JsonObject {
    [key: string]: JsonValue;
}

interface APISourceFormProps {
    initialData?: ApiSourceFormData;
    isEdit?: boolean;
    onSubmit: (data: ApiSourceFormData) => Promise<void>;
    onCancel: () => void;
    onDelete?: () => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
}

interface ResponseParsingConfig {
    type: 'json' | 'xml' | 'csv' | 'text';
    mapping: Record<string, string>;
}

interface ExtendedApiSourceFormData extends Omit<ApiSourceFormData, 'sync_frequency' | 'response_parsing' | 'config'> {
    response_parsing: ResponseParsingConfig;
    config: FormConfig; // Use FormConfig instead of ApiSourceConfig directly
}

interface TestResult {
    success: boolean;
    message: string;
    data?: ApiResponseData;
    rawResponse?: ApiTestResponse;
}

const validateMapping = (mapping: Record<string, string>): string[] => {
    const errors: string[] = [];
    const values = Object.values(mapping);
    const uniqueValues = new Set(values);

    Object.entries(mapping).forEach(([sourcePath, targetField]) => {
        if (!sourcePath.trim()) errors.push('Source path cannot be empty');
        if (!targetField.trim()) errors.push('Target field name cannot be empty');

        const validFieldNameRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/;
        if (!validFieldNameRegex.test(targetField)) {
            errors.push(`Invalid field name format: ${targetField}. Use letters, numbers, and underscores only, starting with a letter.`);
        }
    });

    if (values.length !== uniqueValues.size) {
        errors.push('Duplicate mapped field names are not allowed');
    }

    return [...new Set(errors)];
};

const renderSectionHeader = (icon: React.ReactNode, title: React.JSX.Element) => (
    <div className="d-flex align-items-center mb-2">
        <div className="rounded-circle p-2 bg-secondary bg-opacity-10 me-2">
            {icon}
        </div>
        <h6 className="mb-0 fw-medium">{title}</h6>
    </div>
);

const DEFAULT_PAYLOAD_TEMPLATE = {
    key: "value"
};

const getNestedPaths = (obj: ApiResponseData, prefix = ''): string[] => {
    let paths: string[] = [];

    for (const key in obj) {
        const value = obj[key];
        if (value !== null && typeof value === 'object') {
            if (Array.isArray(value)) {
                paths.push(`${prefix}${key}`);
                if (value.length > 0 && value[0] !== null && typeof value[0] === 'object') {
                    paths.push(`${prefix}${key}[0]`);
                    // Only recurse if the array element is an object
                    paths = paths.concat(getNestedPaths(value[0] as ApiResponseData, `${prefix}${key}[0].`));
                }
            } else {
                paths.push(`${prefix}${key}`);
                paths = paths.concat(getNestedPaths(value as ApiResponseData, `${prefix}${key}.`));
            }
        } else {
            paths.push(`${prefix}${key}`);
        }
    }
    return paths;
};

const evaluatePath = (obj: ApiResponseData, path: string): ApiResponseValue => {
    const parts = path.split('.');
    let currentValue: ApiResponseValue = obj;

    for (const part of parts) {
        if (currentValue === null || typeof currentValue !== 'object') {
            throw new Error('Invalid path');
        }

        const match = part.match(/(\w+)\[(\d+)\]/);
        if (match) {
            const [, key, index] = match;
            const arrayValue: ApiResponseValue[] | undefined = (currentValue as ApiResponseData)[key] as ApiResponseValue[];
            if (!Array.isArray(arrayValue)) {
                throw new Error('Not an array');
            }
            currentValue = arrayValue[parseInt(index)];
        } else {
            currentValue = (currentValue as ApiResponseData)[part];
        }
    }

    return currentValue;
};


const ApiSourceForm: React.FC<APISourceFormProps> = ({
                                                         initialData,
                                                         isEdit = false,
                                                         onSubmit,
                                                         onCancel,
                                                         onDelete,
                                                         error,
                                                         isLoading
                                                     }) => {
    const [formData, setFormData] = useState<ExtendedApiSourceFormData>({
        name: '',
        description: '',
        endpoint: '',
        method: 'POST',
        headers: {},
        authentication: {
            type: 'bearer',
            token: '',
            username: '',
            password: '',
            key: ''
        },
        payload_template: '',
        response_parsing: {
            type: 'json',
            mapping: {}
        },
        config: { ...DEFAULT_CONFIG } as FormConfig // Cast to FormConfig
    });


    const [mappingErrors, setMappingErrors] = useState<string[]>([]);
    const [testLoading, setTestLoading] = useState(false);
    const [tempHeader, setTempHeader] = useState({ key: '', value: '' });
    const [tempMapping, setTempMapping] = useState({ sourcePath: '', targetField: '' });
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [formatError, setFormatError] = useState<string | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [toastPosition, setToastPosition] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (initialData) {
            console.log('[APISourceForm] Initializing form with data:', initialData);

            const initialConfig: FormConfig = {
                requests_per_minute: initialData.config?.requests_per_minute ?? DEFAULT_CONFIG.requests_per_minute,
                retry_count: initialData.config?.retry_count ?? DEFAULT_CONFIG.retry_count,
                timeout_seconds: initialData.config?.timeout_seconds ?? DEFAULT_CONFIG.timeout_seconds,
                max_response_size_mb: initialData.config?.max_response_size_mb ?? DEFAULT_CONFIG.max_response_size_mb
            };

            console.log('[APISourceForm] Processed initial config:', initialConfig);

            setFormData({
                ...initialData,
                response_parsing: {
                    type: initialData.response_parsing?.type || 'json',
                    mapping: initialData.response_parsing?.mapping || {}
                },
                config: initialConfig
            } as ExtendedApiSourceFormData);
        }
    }, [initialData]);

    useEffect(() => {
        const errors = validateMapping(formData.response_parsing.mapping);
        setMappingErrors(errors);
    }, [formData.response_parsing.mapping]);

    const handleParsingChange = (
        field: keyof ResponseParsingConfig,
        value: string | Record<string, string>
    ) => {
        setFormData(prev => ({
            ...prev,
            response_parsing: {
                ...prev.response_parsing,
                [field]: value
            }
        }));
    };


    const handleAddMapping = () => {
        if (tempMapping.sourcePath.trim() && tempMapping.targetField.trim()) {
            setFormData(prev => ({
                ...prev,
                response_parsing: {
                    ...prev.response_parsing,
                    mapping: {
                        ...prev.response_parsing.mapping,
                        [tempMapping.sourcePath]: tempMapping.targetField
                    }
                }
            }));
            setTempMapping({ sourcePath: '', targetField: '' });
        }
    };

    const handleConfigChange = (field: keyof ApiSourceConfig, value: string) => {
        console.log('[APISourceForm] Config change:', { field, value });
        setFormData(prev => {
            const newConfig = {
                ...prev.config,
                [field]: value === '' ? undefined : Number(value)
            };
            console.log('[APISourceForm] Updated config:', newConfig);
            return {
                ...prev,
                config: newConfig
            } as ExtendedApiSourceFormData;
        });
    };

    const removeMapping = (sourcePath: string) => {
        const newMapping = { ...formData.response_parsing.mapping };
        delete newMapping[sourcePath];
        handleParsingChange('mapping', newMapping);
    };

    const handleHeaderChange = (key: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            headers: {
                ...prev.headers,
                [key]: value
            }
        }));
    };

    const handleAddHeader = () => {
        if (tempHeader.key.trim()) {
            handleHeaderChange(tempHeader.key, tempHeader.value);
            setTempHeader({ key: '', value: '' });
        }
    };

    const handleChange = (
        field: keyof Omit<ExtendedApiSourceFormData, 'response_parsing'>,
        value: string | Record<string, string>
    ) => {
        console.log('[APISourceForm] Field change:', { field, value });
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAuthChange = (field: string, value: string) => {
        console.log('[APISourceForm] Auth change:', { field, value });
        setFormData(prev => ({
            ...prev,
            authentication: {
                ...prev.authentication,
                [field]: value
            }
        }));
    };

    const handleTest = async () => {
        if (!formData.endpoint) return;

        console.log('[APISourceForm] Starting API test');
        setTestLoading(true);
        setTestResult(null);

        try {
            // Convert form config to API config
            const processedConfig: ApiSourceConfig = {
                requests_per_minute: formData.config.requests_per_minute ?? DEFAULT_CONFIG.requests_per_minute,
                retry_count: formData.config.retry_count ?? DEFAULT_CONFIG.retry_count,
                timeout_seconds: formData.config.timeout_seconds ?? DEFAULT_CONFIG.timeout_seconds,
                max_response_size_mb: formData.config.max_response_size_mb ?? DEFAULT_CONFIG.max_response_size_mb
            };

            console.log('[APISourceForm] Processed test config:', processedConfig);

            // Prepare test data
            const testData: ApiSourceFormData = {
                ...formData,
                config: processedConfig,
                response_parsing: {
                    ...formData.response_parsing,
                    mapping: formData.response_parsing.mapping
                }
            };

            console.log('[APISourceForm] Sending test request with data:', testData);
            const result = await testAPI(testData);
            console.log('[APISourceForm] Test result:', result);

            setTestResult({
                success: result.success,
                message: formatTestResult(result),
                data: (result.rawResponse?.data || result.data) as ApiResponseData
            });
        } catch (error) {
            console.error('[APISourceForm] Test error:', error);
            setTestResult({
                success: false,
                message: error instanceof Error ? error.message : 'Test failed',
            });
        } finally {
            setTestLoading(false);
        }
    };

    console.log('[APISourceForm] Current form state:', {
        formData,
        mappingErrors,
        testResult,
        isLoading
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('[APISourceForm] Starting form submission');

        const errors = validateMapping(formData.response_parsing.mapping);
        if (errors.length > 0) {
            console.log('[APISourceForm] Validation errors:', errors);
            setMappingErrors(errors);
            return;
        }

        // Convert form config to final API config
        const processedConfig: ApiSourceConfig = {
            requests_per_minute: formData.config.requests_per_minute ?? DEFAULT_CONFIG.requests_per_minute,
            retry_count: formData.config.retry_count ?? DEFAULT_CONFIG.retry_count,
            timeout_seconds: formData.config.timeout_seconds ?? DEFAULT_CONFIG.timeout_seconds,
            max_response_size_mb: formData.config.max_response_size_mb ?? DEFAULT_CONFIG.max_response_size_mb
        };

        console.log('[APISourceForm] Processed submission config:', processedConfig);

        // Create submission data
        const submissionData: ApiSourceFormData = {
            ...formData,
            config: processedConfig,
            response_parsing: {
                ...formData.response_parsing,
                mapping: formData.response_parsing.mapping
            }
        } as ApiSourceFormData;

        console.log('[APISourceForm] Submitting data:', submissionData);
        try {
            await onSubmit(submissionData);
            console.log('[APISourceForm] Submission successful');
        } catch (error) {
            console.error('[APISourceForm] Submission error:', error);
        }
    };

    const suggestNextMapping = () => {
        if (!testResult?.data) return;

        const paths = getNestedPaths(testResult.data);
        const mappedPaths = Object.keys(formData.response_parsing.mapping);
        const unmappedPath = paths.find(path => !mappedPaths.includes(path));

        if (unmappedPath) {
            const suggestedFieldName = unmappedPath.split('.').pop()?.replace(/[[\]0-9]/g, '') || '';
            setTempMapping({
                sourcePath: unmappedPath,
                targetField: suggestedFieldName
            });
        }
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                {error && (
                    <Alert variant="danger" className="mb-3 py-2 px-3">
                        {error}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit} className="mx-auto" style={{ maxWidth: '1100px' }}>
                    {/* Basic Information */}
                    <div className="mb-3">
                        {renderSectionHeader(
                            <Globe size={16} className="text-secondary" />,
                            <>API Source Information <span className="text-danger">*</span></>
                        )}

                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                <span className="text-danger me-1">*</span>
                                Name
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                required
                                className="border"
                                style={{ height: '38px' }}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                className="border"
                            />
                        </Form.Group>
                    </div>

                    {/* API Configuration */}
                    <div className="mb-3">
                        {renderSectionHeader(
                            <Settings size={16} className="text-secondary" />,
                            <>API Configuration <span className="text-danger">*</span></>
                        )}

                        <div className="d-flex gap-3 align-items-start">
                            <div className="flex-grow-1">
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-medium small mb-1">
                                        <span className="text-danger me-1">*</span>
                                        Endpoint URL
                                    </Form.Label>
                                    <Form.Control
                                        type="url"
                                        value={formData.endpoint}
                                        onChange={(e) => handleChange('endpoint', e.target.value)}
                                        required
                                        className="border"
                                        placeholder="https://api.example.com/v1/endpoint"
                                        style={{ height: '38px' }}
                                    />
                                </Form.Group>
                            </div>
                            <div className="mt-4">
                                <Button
                                    variant="outline-primary"
                                    onClick={handleTest}
                                    disabled={testLoading || !formData.endpoint}
                                    className="d-flex align-items-center gap-2"
                                    style={{ height: '38px' }}
                                >
                                    {testLoading ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <Play size={16} />
                                    )}
                                    Test API
                                </Button>
                            </div>
                        </div>

                        {testResult && (
                            <>
                                <Alert
                                    variant={testResult.success ? 'success' : 'danger'}
                                    className="mt-2 mb-3"
                                >
                                    <div>{testResult.message}</div>
                                </Alert>
                                {testResult.success && testResult.data && (
                                    <>
                                        <div className="border rounded p-3 mb-3">
                                            <InlineResponseViewer
                                                data={testResult.data}
                                                onPathCopy={(path, e) => {
                                                    navigator.clipboard.writeText(path);
                                                    setToastPosition({ x: e.clientX, y: e.clientY });
                                                    setShowToast(true);
                                                    setTimeout(() => setShowToast(false), 1000);
                                                }}
                                            />
                                        </div>
                                        <Toast
                                            show={showToast}
                                            onClose={() => setShowToast(false)}
                                            className="position-fixed"
                                            style={{
                                                left: `${toastPosition.x}px`,
                                                top: `${toastPosition.y}px`,
                                                transform: 'translate(-50%, -100%)',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                zIndex: 1000,
                                                border: 'none',
                                                width: 'fit-content',
                                                minWidth: 'unset'
                                            }}
                                        >
                                            <Toast.Body className="p-0">Path copied!</Toast.Body>
                                        </Toast>
                                    </>
                                )}
                            </>
                        )}

                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                <span className="text-danger me-1">*</span>
                                HTTP Method
                            </Form.Label>
                            <Form.Select
                                value={formData.method}
                                onChange={(e) => handleChange('method', e.target.value)}
                                required
                                className="border"
                                style={{ height: '38px', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                            >
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="PUT">PUT</option>
                                <option value="DELETE">DELETE</option>
                                <option value="PATCH">PATCH</option>
                            </Form.Select>
                        </Form.Group>

                        {/* Authentication */}
                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                <span className="text-danger me-1">*</span>
                                Authentication Type
                            </Form.Label>
                            <Form.Select
                                value={formData.authentication.type}
                                onChange={(e) => handleAuthChange('type', e.target.value)}
                                required
                                className="border"
                                style={{ height: '38px', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                            >
                                <option value="none">None</option>
                                <option value="bearer">Bearer Token</option>
                                <option value="basic">Basic Auth</option>
                                <option value="api_key">API Key</option>
                            </Form.Select>
                        </Form.Group>

                        {/* Conditional Authentication Fields */}
                        {formData.authentication.type === 'bearer' && (
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-medium small mb-1">Bearer Token</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={formData.authentication.token}
                                    onChange={(e) => handleAuthChange('token', e.target.value)}
                                    required
                                    className="border"
                                    style={{ height: '38px' }}
                                />
                            </Form.Group>
                        )}

                        {formData.authentication.type === 'basic' && (
                            <>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-medium small mb-1">Username</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.authentication.username}
                                        onChange={(e) => handleAuthChange('username', e.target.value)}
                                        required
                                        className="border"
                                        style={{ height: '38px' }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="fw-medium small mb-1">Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={formData.authentication.password}
                                        onChange={(e) => handleAuthChange('password', e.target.value)}
                                        required
                                        className="border"
                                        style={{ height: '38px' }}
                                    />
                                </Form.Group>
                            </>
                        )}

                        {formData.authentication.type === 'api_key' && (
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-medium small mb-1">API Key</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={formData.authentication.key}
                                    onChange={(e) => handleAuthChange('key', e.target.value)}
                                    required
                                    className="border"
                                    style={{ height: '38px' }}
                                />
                            </Form.Group>
                        )}

                        {/* Headers */}
                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">Headers</Form.Label>
                            <div className="border rounded p-3">
                                {/* Existing headers */}
                                {Object.entries(formData.headers).map(([key, value], index) => (
                                    <div key={index} className="d-flex gap-2 mb-2">
                                        <Form.Control
                                            type="text"
                                            value={key}
                                            disabled
                                            className="border"
                                            style={{ height: '38px', flex: 1 }}
                                        />
                                        <Form.Control
                                            type="text"
                                            value={value}
                                            onChange={(e) => handleHeaderChange(key, e.target.value)}
                                            className="border"
                                            style={{ height: '38px', flex: 1 }}
                                        />
                                        <Button
                                            variant="outline-danger"
                                            onClick={() => {
                                                const newHeaders = { ...formData.headers };
                                                delete newHeaders[key];
                                                handleChange('headers', newHeaders);
                                            }}
                                            style={{ height: '38px', width: '100px' }}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                ))}

                                {/* New header input */}
                                <div className="d-flex gap-2 mb-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Header Key"
                                        value={tempHeader.key}
                                        onChange={(e) => setTempHeader(prev => ({ ...prev, key: e.target.value }))}
                                        className="border"
                                        style={{ height: '38px', flex: 1 }}
                                    />
                                    <Form.Control
                                        type="text"
                                        placeholder="Header Value"
                                        value={tempHeader.value}
                                        onChange={(e) => setTempHeader(prev => ({ ...prev, value: e.target.value }))}
                                        className="border"
                                        style={{ height: '38px', flex: 1 }}
                                    />
                                    <Button
                                        variant="outline-primary"
                                        onClick={handleAddHeader}
                                        style={{ height: '38px', width: '100px' }}
                                    >
                                        Add
                                    </Button>
                                </div>
                            </div>
                        </Form.Group>

                        {/* Payload Template */}
                        <Form.Group className="mb-2">
                            <Form.Label className="fw-medium small mb-1">
                                Payload Template
                            </Form.Label>
                            <div className="d-flex gap-2 align-items-start">
                                <div className="flex-grow-1">
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={formData.payload_template}
                                        onChange={(e) => {
                                            if (!e.target.value) {
                                                setFormatError(null);
                                            }
                                            handleChange('payload_template', e.target.value);
                                        }}
                                        className="border font-monospace"
                                        placeholder={JSON.stringify(DEFAULT_PAYLOAD_TEMPLATE, null, 2)}
                                    />
                                </div>
                                {formData.payload_template && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => {
                                            try {
                                                setFormatError(null);
                                                const parsed = JSON.parse(formData.payload_template || '{}');
                                                handleChange('payload_template', JSON.stringify(parsed, null, 2));
                                            } catch {
                                                setFormatError('Invalid JSON format. Please check your syntax.');
                                            }
                                        }}
                                        style={{ height: '38px', whiteSpace: 'nowrap' }}
                                        title="Format JSON"
                                    >
                                        Format
                                    </Button>
                                )}
                            </div>
                            {formatError ? (
                                <div className="mt-2">
                                    <Alert variant="danger" className="py-2 px-3">
                                        {formatError}
                                    </Alert>
                                </div>
                            ) : (
                                <Form.Text className="text-muted">
                                    JSON template for the request payload. Leave empty for GET requests.
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* Response Parsing */}
                        <div className="mt-4">
                            {renderSectionHeader(
                                <Code size={16} className="text-secondary" />,
                                <>Response Parsing</>
                            )}

                            {mappingErrors.length > 0 && (
                                <Alert variant="warning" className="mb-3">
                                    <div className="fw-bold mb-1">Please fix the following issues:</div>
                                    <ul className="mb-0">
                                        {mappingErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}

                            {/* Response Type */}
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-medium small mb-1">
                                    Response Type
                                </Form.Label>
                                <Form.Select
                                    value={formData.response_parsing.type}
                                    onChange={(e) => handleParsingChange('type', e.target.value as ResponseParsingConfig['type'])}
                                    className="border"
                                    style={{ height: '38px', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                                >
                                    <option value="json">JSON</option>
                                    <option value="xml">XML</option>
                                    <option value="csv">CSV</option>
                                    <option value="text">Plain Text</option>
                                </Form.Select>
                            </Form.Group>

                            {/* Field Mapping */}
                            <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label className="fw-medium small mb-0">
                                        Response Field Mappings
                                    </Form.Label>
                                    {testResult?.data && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={suggestNextMapping}
                                        >
                                            Suggest from Response
                                        </Button>
                                    )}
                                </div>
                                <div className="border rounded p-3">
                                    {/* Existing mappings */}
                                    {Object.entries(formData.response_parsing.mapping).map(([sourcePath, targetField], index) => {
                                        let previewValue = null;
                                        if (testResult?.data) {
                                            try {
                                                const value = evaluatePath(testResult.data, sourcePath);
                                                previewValue = typeof value === 'object'
                                                    ? JSON.stringify(value).slice(0, 50) + (JSON.stringify(value).length > 50 ? '...' : '')
                                                    : String(value);
                                            } catch {
                                                previewValue = 'Not found in response';
                                            }
                                        }

                                        return (
                                            <div key={index} className="mb-3">
                                                <div className="d-flex gap-2 mb-1">
                                                    <div className="flex-grow-1">
                                                        <Form.Control
                                                            type="text"
                                                            value={sourcePath}
                                                            disabled
                                                            className="border"
                                                            style={{ height: '38px' }}
                                                        />
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <Form.Control
                                                            type="text"
                                                            value={targetField}
                                                            disabled
                                                            className="border"
                                                            style={{ height: '38px' }}
                                                        />
                                                    </div>
                                                    <Button
                                                        variant="outline-danger"
                                                        onClick={() => removeMapping(sourcePath)}
                                                        style={{ height: '38px', width: '100px' }}
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                                {testResult?.data && (
                                                    <div className="ms-2 small text-muted d-flex align-items-center">
                                                        <span className="me-2">Preview:</span>
                                                        <code className="bg-light px-2 py-1 rounded">
                                                            {previewValue}
                                                        </code>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    {/* New mapping input */}
                                    <div className="d-flex gap-2 mb-2">
                                        <div className="flex-grow-1">
                                            <Form.Control
                                                type="text"
                                                placeholder="Source Path (e.g., data.results[0].id)"
                                                value={tempMapping.sourcePath}
                                                onChange={(e) => {
                                                    setTempMapping(prev => ({ ...prev, sourcePath: e.target.value }));
                                                }}
                                                className="border"
                                                style={{ height: '38px' }}
                                            />
                                            {testResult?.data && tempMapping.sourcePath && (
                                                <div className="small text-muted mt-1">
                                                    Preview: <code className="bg-light px-2 py-1 rounded">
                                                    {(() => {
                                                        try {
                                                            const value = evaluatePath(testResult.data, tempMapping.sourcePath);
                                                            return typeof value === 'object'
                                                                ? JSON.stringify(value).slice(0, 50) + (JSON.stringify(value).length > 50 ? '...' : '')
                                                                : String(value);
                                                        } catch {
                                                            return 'Not found in response';
                                                        }
                                                    })()}
                                                </code>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Form.Control
                                                type="text"
                                                placeholder="Target Field Name"
                                                value={tempMapping.targetField}
                                                onChange={(e) => setTempMapping(prev => ({ ...prev, targetField: e.target.value }))}
                                                className="border"
                                                style={{ height: '38px' }}
                                            />
                                        </div>
                                        <Button
                                            variant="outline-primary"
                                            onClick={handleAddMapping}
                                            style={{ height: '38px', width: '100px' }}
                                        >
                                            Add
                                        </Button>
                                    </div>

                                </div>

                            </Form.Group>

                            {/* Add Rate Limiting & Performance Settings */}
                            <div className="mb-3">
                                {renderSectionHeader(
                                    <Clock size={16} className="text-secondary" />,
                                    <>Rate Limiting & Performance</>
                                )}

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label className="fw-medium small mb-1">
                                                Requests per Minute
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="1000"
                                                    value={formData.config.requests_per_minute?.toString() || ''}
                                                    onChange={(e) => handleConfigChange('requests_per_minute', e.target.value)}
                                                    className="border"
                                                    style={{height: '38px'}}
                                                />
                                                <InputGroup.Text>requests/min</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Limit: 1-1000 requests per minute
                                                (Default: {DEFAULT_CONFIG.requests_per_minute})
                                            </Form.Text>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label className="fw-medium small mb-1">
                                                Timeout
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="300"
                                                    value={formData.config.timeout_seconds?.toString() || ''}
                                                    onChange={(e) => handleConfigChange('timeout_seconds', e.target.value)}
                                                    className="border"
                                                    style={{height: '38px'}}
                                                />
                                                <InputGroup.Text>seconds</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Limit: 1-300 seconds (Default: {DEFAULT_CONFIG.timeout_seconds})
                                            </Form.Text>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label className="fw-medium small mb-1">
                                                Retry Count
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max="5"
                                                    value={formData.config.retry_count?.toString() || ''}
                                                    onChange={(e) => handleConfigChange('retry_count', e.target.value)}
                                                    className="border"
                                                    style={{height: '38px'}}
                                                />
                                                <InputGroup.Text>retries</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Number of retry attempts (0-5) (Default: {DEFAULT_CONFIG.retry_count})
                                            </Form.Text>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group>
                                            <Form.Label className="fw-medium small mb-1">
                                                Max Response Size
                                            </Form.Label>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    value={formData.config.max_response_size_mb?.toString() || ''}
                                                    onChange={(e) => handleConfigChange('max_response_size_mb', e.target.value)}
                                                    className="border"
                                                    style={{height: '38px'}}
                                                />
                                                <InputGroup.Text>MB</InputGroup.Text>
                                            </InputGroup>
                                            <Form.Text className="text-muted">
                                                Maximum response size (1-100 MB)
                                                (Default: {DEFAULT_CONFIG.max_response_size_mb})
                                            </Form.Text>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Form Actions */}
                        <div className="d-flex justify-content-between align-items-center border-top pt-3 mt-3">
                            {isEdit && onDelete ? (
                                <Button
                                    variant="danger"
                                    onClick={onDelete}
                                    disabled={isLoading}
                                    style={{height: '38px'}}
                                >
                                    Delete
                                </Button>
                            ) : <div/>}

                            <div className="d-flex gap-2">
                                <Button
                                    variant="light"
                                    onClick={onCancel}
                                    disabled={isLoading}
                                    style={{ height: '38px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isLoading}
                                    style={{ height: '38px' }}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" />
                                            {isEdit ? 'Saving...' : 'Creating...'}
                                        </>
                                    ) : (
                                        isEdit ? 'Save' : 'Create API Source'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>

                </Form>
            </Card.Body>
        </Card>
    );
};

export default ApiSourceForm;